import { type ReactNode } from "react";

import {
  Box,
  Column,
  ExternalLinkIcon,
  FormField,
  InformationIcon,
  Paragraph,
  Row,
  Select,
  SparkleIcon,
  Switch,
  Text,
  Tooltip,
} from "@hightouch/htui";
import { Link } from "src/router";
import * as analytics from "src/lib/analytics";

import { FormkitModel } from "../formkit-context";
import { isConversionSyncType } from "src/utils/match-boosting";
import { DestinationDefinitionFragment } from "src/graphql";
import {
  LearnMoreToDocsLink,
  LearnMoreToMarketingLink,
  ModelSetUpLink,
} from "src/pages/syncs/sync/components/shared-match-booster";
import {
  EU_HOUSEHOLD_TARGETING_MESSAGE,
  type MatchBoosterRegion,
} from "src/utils/match-booster-regions";

export enum TargetingLevel {
  Person = "person",
  Household = "household",
}

const getTargetingLevelOptions = (householdTargetingEnabled: boolean) => [
  { label: "Person", value: TargetingLevel.Person },
  {
    label: "Household",
    value: TargetingLevel.Household,
    disabled: !householdTargetingEnabled,
  },
];

const TARGETING_LEVEL_HELP_MESSAGE =
  "Person-level targeting matches your records to a single individual using precise identifiers like name, email, phone, and device IDs. Household-level targeting expands targeting to all people and devices located at the same residential address.";

const TARGETING_LEVEL_SYNC_RAN_MESSAGE =
  "Targeting level cannot be edited after the first sync. To use a different targeting level after the sync is active, create a new sync with a different targeting level.";

const TARGETING_LEVEL_TEMPLATE_RAN_MESSAGE =
  "Targeting level can't be changed after a sync using this template has run. To change it, create a new template.";

const TARGETING_LEVEL_EU_MESSAGE = EU_HOUSEHOLD_TARGETING_MESSAGE;

/**
 * A template never runs on its own, so `syncRan` is always false on the
 * template form. Its dependent syncs are what pin the targeting level, and
 * they need their own message because the remedy is a new template rather
 * than a new sync. Pinned-after-run messages win over the EU message, since
 * a sync that already ran can't switch region either.
 */
export function getTargetingLevelMessage({
  syncRan,
  hasDependentSyncRun,
  isEuRegion,
}: {
  syncRan: boolean;
  hasDependentSyncRun: boolean;
  isEuRegion: boolean;
}): string {
  if (syncRan) {
    return TARGETING_LEVEL_SYNC_RAN_MESSAGE;
  }
  if (hasDependentSyncRun) {
    return TARGETING_LEVEL_TEMPLATE_RAN_MESSAGE;
  }
  if (isEuRegion) {
    return TARGETING_LEVEL_EU_MESSAGE;
  }
  return TARGETING_LEVEL_HELP_MESSAGE;
}

export const MatchBoosterMappingToggle: React.FC<
  Readonly<{
    matchBoostingEnabled: boolean;
    matchBoosterRegionControl?: ReactNode;
    mbEnabledForWorkspace: boolean;
    mbAvailableForSyncConfig: boolean;
    model: FormkitModel | undefined;
    workspaceId: number | undefined;
    syncId: number | undefined;
    onToggle: () => void;
    config: { type?: string; [x: string]: unknown };
    destinationDefinition: DestinationDefinitionFragment | undefined;
    isDisabled?: boolean;
    syncRan: boolean;
    /** True when any sync built from the template being edited has run. */
    hasDependentSyncRun?: boolean;
    householdTargetingEnabled?: boolean;
    matchBoosterRegion?: MatchBoosterRegion;
    targetingLevel?: TargetingLevel;
    onTargetingLevelChange?: (level: TargetingLevel) => void;
  }>
> = ({
  matchBoostingEnabled,
  matchBoosterRegionControl,
  mbEnabledForWorkspace,
  mbAvailableForSyncConfig,
  model,
  syncId,
  workspaceId,
  onToggle,
  config,
  destinationDefinition,
  isDisabled = false,
  syncRan = false,
  hasDependentSyncRun = false,
  householdTargetingEnabled = false,
  matchBoosterRegion,
  targetingLevel,
  onTargetingLevelChange,
}) => {
  const mbEnabledForModel = Boolean(
    model?.parent?.matchboosting_enabled || model?.matchboosting_enabled,
  );

  // Return nothing if the destination doesn't support MB or enrichment hasn't
  // ran yet, even if model has MB enabled, or if a sync is already created for
  // a non-MB workspace
  if (
    !mbAvailableForSyncConfig ||
    // We only want to display the up-sell for non-MB customers when creating a sync
    (!mbEnabledForWorkspace && syncId)
  ) {
    return null;
  }

  const mbAvailableToToggle =
    mbAvailableForSyncConfig && mbEnabledForWorkspace && mbEnabledForModel;

  const isConversionType = isConversionSyncType(
    config?.type,
    destinationDefinition?.type,
  );

  const modelId = model?.id;

  const isTargetingLevelPinned = syncRan || hasDependentSyncRun;
  const targetingLevelMessage = getTargetingLevelMessage({
    syncRan,
    hasDependentSyncRun,
    isEuRegion: matchBoosterRegion === "eu",
  });

  return (
    <Row
      borderWidth="hairline"
      borderStyle="solid"
      borderRadius="6px"
      borderColor="gray.400"
      mt={3}
      mb={4}
      p={4}
      display="flex"
      gap={4}
    >
      <Column>
        <Switch
          aria-label="Enable match booster."
          isChecked={matchBoostingEnabled}
          onChange={onToggle}
          isDisabled={isDisabled || !mbAvailableToToggle}
        />
      </Column>

      <Column gap={2}>
        <Row gap={1} pt="2px">
          <Text
            color={!mbAvailableToToggle ? "text.secondary" : "text.primary"}
            fontWeight="medium"
          >
            {getTitle({
              mbAvailableToToggle,
              mbEnabledForWorkspace,
              isConversionType,
            })}
          </Text>
          <Box as={SparkleIcon} color="warning.400" fontSize="20px" />
        </Row>

        <Paragraph>
          {getContent({
            mbAvailableToToggle,
            mbEnabledForWorkspace,
            isConversionType,
          })}{" "}
          {mbAvailableToToggle ? (
            getLinkWhenMbAvailable({ isConversionType, workspaceId, modelId })
          ) : mbEnabledForWorkspace ? (
            <ModelSetUpLink model={model} />
          ) : (
            <LearnMoreToMarketingLink
              modelId={modelId}
              workspaceId={workspaceId}
              appComponent="MatchBoosterMappingToggle"
            />
          )}
        </Paragraph>

        {matchBoostingEnabled && (
          <Column gap={4} mt={2}>
            <Column flexShrink={0} width="4xs">
              <FormField label="Targeting level">
                <Row alignItems="center" gap={2}>
                  <Column flex={1} minWidth={0}>
                    <Select
                      options={getTargetingLevelOptions(
                        householdTargetingEnabled,
                      )}
                      value={targetingLevel ?? TargetingLevel.Person}
                      width="100%"
                      isDisabled={isDisabled || isTargetingLevelPinned}
                      // Select ignores option.disabled unless isOptionDisabled is set.
                      isOptionDisabled={(option) => Boolean(option.disabled)}
                      onChange={(value) => {
                        onTargetingLevelChange?.(value as TargetingLevel);
                      }}
                    />
                  </Column>
                  <Tooltip message={targetingLevelMessage}>
                    <Box
                      as={InformationIcon}
                      color="text.secondary"
                      role="img"
                      aria-label="More information about targeting level"
                    />
                  </Tooltip>
                </Row>
              </FormField>
            </Column>
            {matchBoosterRegionControl && (
              <Column flexShrink={0} gap={4} width="4xs">
                {matchBoosterRegionControl}
              </Column>
            )}
          </Column>
        )}
      </Column>
    </Row>
  );
};

const getTitle = ({
  mbAvailableToToggle,
  mbEnabledForWorkspace,
  isConversionType,
}: {
  mbAvailableToToggle: boolean;
  mbEnabledForWorkspace: boolean;
  isConversionType: boolean;
}) => {
  if (mbAvailableToToggle) return "Match Booster";

  if (isConversionType) {
    return mbEnabledForWorkspace
      ? "Enable Match Booster for better conversion performance"
      : "Increase your conversion performance!";
  }

  return mbEnabledForWorkspace
    ? "Enable Match Booster for higher audience reach"
    : "Increase your audience size!";
};

const getContent = ({
  isConversionType,
  mbAvailableToToggle,
  mbEnabledForWorkspace,
}: {
  isConversionType: boolean;
  mbAvailableToToggle: boolean;
  mbEnabledForWorkspace: boolean;
}) => {
  if (isConversionType) {
    return mbEnabledForWorkspace ? (
      <Text>
        Improve return on ad spend (ROAS) across conversion-focused campaigns by
        enhancing your event data with Hightouch’s cookieless identity graph.
      </Text>
    ) : (
      <Text>
        Improve return on ad spend (ROAS) across conversion-focused campaigns by
        enhancing your event data with Hightouch’s cookieless identity graph -
        feeding better data to the AI algorithms that manage budgets, bids, and
        targeting.
      </Text>
    );
  }

  return mbAvailableToToggle ? (
    <Text>
      Increase your audience match rates and reach by enhancing records with
      Hightouch’s cookieless identity graph.
    </Text>
  ) : (
    <Text>
      Increase your audience reach with Hightouch’s cookieless identity graph.
      Drive incremental revenue from broader targeting and reduce wasted spend
      with better suppression.
    </Text>
  );
};

const getLinkWhenMbAvailable = ({
  isConversionType,
  workspaceId,
  modelId,
}: {
  isConversionType: boolean;
  workspaceId: string | number | undefined;
  modelId: string | number | undefined;
}) => {
  if (isConversionType) {
    return (
      <Text
        onClick={() => {
          analytics.track(
            "Match Booster Conversion Performance Clicked In-App",
            {
              workspace_id: workspaceId?.toString(),
              model_id: modelId?.toString(),
              app_component: "MatchBoosterMappingToggle",
            },
          );
        }}
      >
        <Link
          href="https://hightouch.com/docs/match-booster/offerings#conversion-event-boosting"
          isExternal
        >
          Learn more about Match Booster for conversions
          <Box as={ExternalLinkIcon} ml={1} mb="1px" />
        </Link>
      </Text>
    );
  }

  return (
    <LearnMoreToDocsLink
      modelId={modelId}
      workspaceId={workspaceId}
      appComponent="MatchBoosterMappingToggle"
    />
  );
};
